import type { ReactNode, SVGProps } from 'react';

/**
 * Inline SVG icon set (no icon library — self-contained per spec).
 *
 * All icons:
 * - inherit colour via `stroke="currentColor"` / `fill` where noted, so callers
 *   control colour through text/utility classes;
 * - are decorative by default (`aria-hidden`), because they always accompany a
 *   text label. Callers that use an icon as the sole content must pass an
 *   accessible name on the surrounding control instead.
 */

/** Common props shared by every icon; defaults to 1em square, decorative. */
type IconProps = SVGProps<SVGSVGElement>;

/**
 * Base wrapper standardising size, stroke and the decorative default.
 * `size` sets both width and height (px) and can be overridden per-call.
 */
function Icon({
  children,
  size = 20,
  strokeWidth = 2,
  ...props
}: IconProps & { size?: number; children: ReactNode }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Check mark — healthy / success. */
export function CheckIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M20 6 9 17l-5-5" />
    </Icon>
  );
}

/** Warning triangle — degraded / caution. */
export function WarningIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Icon>
  );
}

/** Octagon with slash — critical / failure. */
export function CriticalIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2Z" />
      <path d="M15 9l-6 6" />
      <path d="M9 9l6 6" />
    </Icon>
  );
}

/** Question mark in a circle — unknown / indeterminate. */
export function UnknownIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </Icon>
  );
}

/** Info circle — neutral information. */
export function InfoIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </Icon>
  );
}

/** Bell — notifications. */
export function BellIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </Icon>
  );
}

/** Spinner arc — in-flight / checking. Animate via the `animate-spin` class. */
export function SpinnerIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </Icon>
  );
}

/** Sun — light theme. */
export function SunIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </Icon>
  );
}

/** Moon — dark theme. */
export function MoonIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </Icon>
  );
}

/** Half-filled circle — auto (follow system) theme. */
export function AutoThemeIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18" />
      <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Magnifying glass — search / global find. */
export function SearchIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </Icon>
  );
}

/** Hamburger — open the mobile navigation. */
export function MenuIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </Icon>
  );
}

/** X — close / dismiss. */
export function CloseIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Icon>
  );
}

/** Pencil — edit an item (compact row action). */
export function PencilIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </Icon>
  );
}

/** Trash can — delete / remove an item (compact row action). */
export function TrashIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </Icon>
  );
}

/** Chevron pointing down — expand a menu. */
export function ChevronDownIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  );
}

/** Chevron pointing left — previous / go back. */
export function ChevronLeftIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="m15 18-6-6 6-6" />
    </Icon>
  );
}

/** Chevron pointing right — next / drill in. */
export function ChevronRightIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="m9 18 6-6-6-6" />
    </Icon>
  );
}

/** Door with arrow — sign out. */
export function SignOutIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </Icon>
  );
}

/** Circled question mark — HelpTip trigger. */
export function QuestionIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </Icon>
  );
}

/** Gauge — Dashboard. */
export function DashboardIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
      <path d="M12 4a8 8 0 0 0-8 8 8 8 0 0 0 2.34 5.66" />
      <path d="M20 12a8 8 0 0 0-8-8" />
      <path d="M17.66 17.66A8 8 0 0 0 20 12" />
      <path d="m13.9 10.1 3.6-3.6" />
    </Icon>
  );
}

/** People — Customers. */
export function CustomersIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  );
}

/** Stacked layers — Applications. */
export function ApplicationsIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </Icon>
  );
}

/** Server stack — Environments. */
export function EnvironmentsIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="6" rx="1" />
      <rect x="3" y="14" width="18" height="6" rx="1" />
      <path d="M7 7h.01" />
      <path d="M7 17h.01" />
    </Icon>
  );
}

/** Tag — Releases. */
export function ReleasesIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.42l8.82 8.82a2 2 0 0 0 2.82 0l7.17-7.17a2 2 0 0 0 0-2.82Z" />
      <path d="M7 7h.01" />
    </Icon>
  );
}

/** Rocket — Deployments. */
export function DeploymentsIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </Icon>
  );
}

/** Terminal box — Agents. */
export function AgentsIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <path d="m7 8 3 3-3 3" />
      <path d="M13 14h4" />
    </Icon>
  );
}

/** Shield — Security. */
export function SecurityIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  );
}

/** Bar chart — Reports. */
export function ReportsIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M3 3v18h18" />
      <path d="M7 16v-4" />
      <path d="M12 16V8" />
      <path d="M17 16v-7" />
    </Icon>
  );
}

/** Heartbeat/pulse line — Monitoring (certificate + disk health, item 7.8). */
export function MonitoringIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M3 12h4l2 6 4-14 2 8h6" />
    </Icon>
  );
}

/** Life-buoy — Help. */
export function HelpIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" />
      <path d="m4.93 4.93 4.24 4.24" />
      <path d="m14.83 9.17 4.24-4.24" />
      <path d="m14.83 14.83 4.24 4.24" />
      <path d="m4.93 19.07 4.24-4.24" />
    </Icon>
  );
}

/** Compass — start a guided tour. */
export function CompassIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12Z" />
    </Icon>
  );
}

/** Sliders — Administration. */
export function AdministrationIcon(props: IconProps & { size?: number }): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M4 21v-7" />
      <path d="M4 10V3" />
      <path d="M12 21v-9" />
      <path d="M12 8V3" />
      <path d="M20 21v-5" />
      <path d="M20 12V3" />
      <path d="M2 14h4" />
      <path d="M10 8h4" />
      <path d="M18 16h4" />
    </Icon>
  );
}
